import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { MaterialSymbol } from '../../components';

import css from './OccupancyBanner.module.css';

// Occupancy tiers for the "high demand" banner. Below 40% (or unknown) no banner
// is shown. The percentage is compared after rounding (done upstream).
//   low    40–59  🟡
//   medium 60–79  🟠 (adds a "book now" note)
//   high   80–100 🔴
const resolveTier = percentage => {
  if (percentage == null || percentage < 40) return null;
  if (percentage < 60) return { id: 'low', hasNote: false, showFire: false };
  if (percentage < 80) return { id: 'medium', hasNote: true, showFire: true };
  return { id: 'high', hasNote: false, showFire: true };
};

/**
 * Urgency banner shown on the search results, driven by the searched province's
 * occupancy for the searched date. Renders nothing when the percentage is unknown
 * or below the 40% threshold.
 *
 * @param {Object} props
 * @param {number|null} props.percentage rounded province occupancy (0–100).
 * @param {'map'|'grid'} [props.variant] search layout, used to align the banner
 *   with the results column (horizontal inset + balanced vertical spacing).
 */
const OccupancyBanner = props => {
  const { rootClassName, className, percentage, variant } = props;
  const tier = resolveTier(percentage);

  if (!tier) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, variant ? css[variant] : null, className);

  return (
    <div className={classes} data-tier={tier.id} role="status">
      <span className={css.icon} aria-hidden="true">
        <MaterialSymbol name="thermometer" size={42} fill={1} weight={500} />
      </span>
      <div className={css.content}>
        <p className={css.title}>
          {tier.showFire ? (
            <span className={css.fire} aria-hidden="true">
              🔥{' '}
            </span>
          ) : null}
          <FormattedMessage id={`OccupancyBanner.title.${tier.id}`} />
        </p>
        <p className={css.body}>
          <FormattedMessage id={`OccupancyBanner.body.${tier.id}`} values={{ x: percentage }} />
        </p>
        {tier.hasNote ? (
          <p className={css.note}>
            <FormattedMessage id={`OccupancyBanner.note.${tier.id}`} />
          </p>
        ) : null}
      </div>
    </div>
  );
};

const { number, oneOf, string } = PropTypes;

OccupancyBanner.defaultProps = {
  rootClassName: null,
  className: null,
  percentage: null,
  variant: null,
};

OccupancyBanner.propTypes = {
  rootClassName: string,
  className: string,
  percentage: number,
  variant: oneOf(['map', 'grid']),
};

export default OccupancyBanner;
